import React, { Component } from "react"
import { connect } from "react-redux"
import PropTypes from "prop-types"
import { withRouter, Link } from "react-router-dom"
import SearchResult from "../components/SearchResult"
import { globalSearch, populateStateWithQuery } from "../actions/searchActions"
import { getInitial, formatNumber } from "../util/string"
import LoadingSpinner from "../components/LoadingSpinner"
import InfoModal from "../components/InfoModal"
import Styles from "./SearchResults.module.css"
import { scrolledToBottomListener } from "../util/handleScrollToBottom"
import { Seo } from "../components/Seo"

class SearchResults extends Component {
  state = {
    items: [],
  }

  componentDidMount() {
    this.props.populateState(this.props.location)
    this.initScrollHandler()
  }

  componentDidUpdate(prevProps) {
    const { searchResult, location, searching } = this.props

    if (location !== prevProps.location) {
      this.handleNewSearch()
    }

    if (!searching && prevProps.searching !== searching) {
      this.clearItems()
      if (searchResult.length) {
        this.appendRenderableItems(0, 50)
      }
    }
  }

  componentWillUnmount() {
    this._scrollListener.stop()
  }

  handleNewSearch = () => {
    const { search, searchString, filters } = this.props

    this.clearItems()
    search(searchString, filters)
  }

  clearItems = () => {
    this.setState({ items: [] })
  }

  initScrollHandler = () => {
    this._scrollListener = scrolledToBottomListener(() => {
      const { items } = this.state
      this.appendRenderableItems(items.length, items.length + 50)
    })
    this._scrollListener.start()
  }

  appendRenderableItems = (start, end) => {
    const { searchResult } = this.props
    const { items } = this.state
    if (start === 0) return this.setState({ items: searchResult.slice(start, end) })
    this.setState({ items: items.concat(searchResult.slice(start, end)) })
  }

  renderResults = () => {
    const { location, searching } = this.props
    const { items } = this.state

    if (searching) return <LoadingSpinner loading={searching} />

    return items.map((result, i, arr) => {
      if (
        i === 0 ||
        getInitial(result.virallinen_nimi) !== getInitial(arr[i - 1].virallinen_nimi)
      ) {
        return (
          <div key={result.profinder_id || result.y_tunnus}>
            <div className={Styles.divider}>
              <h2>
                <span>{getInitial(result.virallinen_nimi)}</span>
              </h2>
            </div>
            <SearchResult
              key={result.profinder_id || result.y_tunnus}
              result={result}
              location={location}
            />
          </div>
        )
      }
      return (
        <SearchResult
          key={result.profinder_id || result.y_tunnus}
          result={result}
          location={location}
        />
      )
    })
  }

  // Forms filter substring, query[0] is keyword and query[1] the value
  formSub = query => {
    if (query[0] === "personnels[]" || query[0] === "revenues[]") {
      if (query[1] === "-1,-1") return "Ei tiedossa"
      if (query[1] === '0,0') return '0'
      return query[1]
        .split(",")
        .map(n => formatNumber(n))
        .join(" - ")
    }
    if (query[0] === "location[]" && query[1] === 'home') return 'Kotikunta (yritykset, joiden kotipaikka on Tampereen kaupunkiseudulla)'
    if (query[0] === "location[]" && query[1] === 'branch') return 'Sijainti (yrityksiä, joilla sijaitsee toimipaikka Tampereen kaupunkiseudulla)'
    if (query[0] === "location[]" && query[1] === 'all') return 'Molemmat (kotipaikan ja sijainnin mukaan)'
    if (query[0] === "office[]") {
      if (query[1] === 'main_office') return 'Päätoimipaikka'
      if (query[1] === 'branch') return 'Sivutoimipaikka'
    }
    return query[1]
  }

  renderSearchFilters = () => {
    const { location } = this.props

    const keywords = {
      "region[]": "Kunta",
      "location[]": "Kunta",
      "office[]": "Toimipaikka",
      "industry[]": "Toimiala",
      "service[]": "Palvelu",
      "form[]": "Yhtiömuoto",
      startYearFrom: "Vuodesta",
      startYearTo: "Vuoteen",
      "personnels[]": "Henkilöstö",
      "revenues[]": "Liikevaihto",
    }

    const params = new URLSearchParams(location.search)

    // Key - values mapping
    const valuesByKey = {}
    for (const [key, value] of params.entries()) {
      if (!valuesByKey[key]) valuesByKey[key] = []
      valuesByKey[key].push(decodeURIComponent(value))
    }

    if (Object.keys(valuesByKey).length === 0) return null

    const compressKeys = new Set([
      "region[]",
      "office[]",
      "industry[]",
      "service[]",
      "form[]",
      "personnels[]",
      "revenues[]",
    ])

    const regionValues = valuesByKey["region[]"] || []
    const locationValues = valuesByKey["location[]"] || []

    // Format each individual value
    const formatOne = (key, rawValue) => this.formSub([key, rawValue])

    // Compose like: Location (region1, region2)
    const composedLocationValue =
      locationValues.length > 0
        ? (() => {
            const loc = locationValues.map(v => formatOne("location[]", v)).join(", ")
            const reg =
              regionValues.length > 0
                ? regionValues.map(v => formatOne("region[]", v)).join(", ")
                : ""
            return reg ? `${loc} - ${reg}` : loc
          })()
        : null

    const items = []

    // Add composed location row (if location exists)
    if (composedLocationValue) {
      items.push({
        key: "__composed_location__",
        label: keywords["location[]"],
        value: composedLocationValue,
      })
    }

    for (const key of Object.keys(valuesByKey)) {
      // Skip location (handled above)
      if (key === "location[]") continue
      // If we rendered composed location, skip region so it doesn't duplicate
      if (key === "region[]" && composedLocationValue) continue

      const label = keywords[key]
      if (!label) continue

      const rawValues = valuesByKey[key]
      const formatted = rawValues.map(v => formatOne(key, v))

      const value = compressKeys.has(key)
        ? formatted.join(", ")
        : formatted[formatted.length - 1]

      items.push({ key, label, value })
    }

    if (items.length === 0) return null

    return (
      <div className={Styles.usedFilters}>
        {items.map(item => (
          <div key={item.key} className={Styles.filter}>
            <p>
              <strong>{`${item.label}: `}</strong>
              {item.value}
            </p>
          </div>
        ))}
      </div>
    )
  }

  getPageTitle = () => {
    const {
      match: {
        params: { searchValue },
      },
    } = this.props
    let keyword = searchValue && searchValue !== '-' ? `"${searchValue}"` : '';

    return `Hakutulokset ${keyword}`
  }
  render() {
    const { searching, searchResult } = this.props

    const uniqueBusinessIds = new Set();
    let totalOffices = 0;
    searchResult?.forEach(r => {
      if (!uniqueBusinessIds.has(r.y_tunnus)) {
        uniqueBusinessIds.add(r.y_tunnus);
      }
      totalOffices += 1;
    })

    return (
      <div className={[Styles.pageContent, Styles.resultScreen].join(" ")}>
        <div>
          <div className={Styles.backButton}>
            <Link to="/">← Takaisin etusivulle</Link>
          </div>
          <div className={Styles.searchInfoWrapper}>
            {searching ? null : (
              <div className={Styles.searchInfo}>
                Haulla löytyi <span className={Styles.searchInfoResult}>{`${uniqueBusinessIds.size} `}</span>
                {uniqueBusinessIds.size === 1 ? "aktiivinen yritys" : "yksittäistä aktiivista yritystä"}
                <InfoModal position="left">
                  <p className={Styles.infoModalText}>
                    Yritys on aktiivinen kun se on rekisteröity kaupparekisteriin sekä vähintään yhteen toiseen rekisteriin
                    (ALV-rekisteriin, ennakkoperintärekisteriin, tai työnantajarekisteriin) tai yrityksellä on vähintään
                    yksi tilinpäätös viimeiseltä kolmelta tilikaudelta, eikä sillä ole mitään erityistilannetta (konkurssi,
                    saneeraus, selvitystila, toiminta keskeytynyt tai toiminta lakannut).
                    Viimeisen kuuden kuukauden sisään perustetut yritykset ovat automaattisesti aktiivisia.
                    <br />
                    <br />
                    Sisältää seuraavat yhtiömuodot: OY, OYJ, TMI, KY, AY, OSK.
                    <br />
                    <br />
                    Tietolähde: Profinder
                  </p>
                </InfoModal> ja
                <span className={Styles.searchInfoResult}>{` ${totalOffices} `}</span>
                {totalOffices === 1 ? "toimipaikka" : "toimipaikkaa"}
              </div>
            )}
          </div>
          {this.renderSearchFilters()}
          <div className={Styles.resultWrapper}>
            <div className={Styles.results}>{this.renderResults()}</div>
          </div>
        </div>
        <Seo title={this.getPageTitle()} />
      </div>
    )
  }
}

SearchResults.propTypes = {
  search: PropTypes.func.isRequired,
  populateState: PropTypes.func.isRequired,
  searching: PropTypes.bool.isRequired,
  searchResult: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  match: PropTypes.shape().isRequired,
  filters: PropTypes.shape().isRequired,
  searchedValue: PropTypes.string,
  searchString: PropTypes.string.isRequired,
  location: PropTypes.shape({ pathname: PropTypes.string, search: PropTypes.string }).isRequired,
}

const mapStateToProps = state => ({
  searching: state.search.loading,
  searchResult: state.search.result,
  searchedValue: state.search.value,
  filters: state.search.filters,
  searchString: state.search.searchString,
})

const mapDispatchToProps = dispatch => ({
  search: (ss, f) => dispatch(globalSearch(ss, f)),
  populateState: location => dispatch(populateStateWithQuery(location)),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SearchResults))
